import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { BookOpen, ArrowLeft, KeyRound, Copy, Check, Crown, Gift, Globe } from "lucide-react";
import { useLanguage } from "../hooks/useLanguage";
import LanguageToggle from "./LanguageToggle";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("annual");
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const plans = [
    {
      id: "monthly",
      name: "Monthly Plan",
      price: 9.99,
      interval: "month",
      description: "7-day free trial, then billed monthly"
    },
    {
      id: "annual",
      name: "Annual Plan",
      price: 69.99,
      interval: "year",
      description: "Best value! 7-day free trial, then billed yearly",
      savings: "42%"
    }
  ];

  const [redirecting, setRedirecting] = useState(false);
  const [buttonText, setButtonText] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await axios.post(`${API}/auth/login`, { email, password });
        login(data.token, data.user);
        navigate("/parent");
      } else {
        setButtonText("Creating account...");
        
        // Register user
        const { data } = await axios.post(`${API}/auth/register`, { 
          name, 
          email, 
          password
        });
        
        setButtonText("Setting up payment...");
        
        // Create checkout session using the new token (without logging in)
        const checkoutRes = await axios.post(`${API}/subscription/checkout`, {
          plan_type: selectedPlan,
          success_url: `${window.location.origin}/subscription/success`,
          cancel_url: `${window.location.origin}/auth`
        }, {
          headers: { Authorization: `Bearer ${data.token}` }
        });
        
        // Store credentials temporarily for after payment
        // Using localStorage because sessionStorage doesn't persist across domain redirects
        localStorage.setItem("pending_auth", JSON.stringify({ token: data.token, user: data.user }));
        
        setRedirecting(true);
        
        // Small delay to show redirecting message before actual redirect
        setTimeout(() => {
          window.location.href = checkoutRes.data.checkout_url;
        }, 500);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "An error occurred");
      setLoading(false);
      setRedirecting(false);
      setButtonText("");
    }
  };

  // Show redirecting screen
  if (redirecting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#118AB2] via-[#06D6A0] to-[#FFD166] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl text-center max-w-sm">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#06D6A0] border-t-transparent mx-auto mb-4"></div>
          <h2 className="text-xl font-black text-[#073B4C] mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
            Redirecting to Payment...
          </h2>
          <p className="text-gray-500 text-sm">
            You'll be redirected to our secure payment page to enter your card details.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#118AB2] via-[#06D6A0] to-[#FFD166] flex items-center justify-center p-4">
      <div className="w-full max-w-5xl">
        {/* Top bar with back and language toggle */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/80 hover:text-white font-semibold transition-colors"
            data-testid="back-to-landing"
          >
            <ArrowLeft size={20} />
            {t('back')}
          </button>
          <LanguageToggle variant="landing" />
        </div>

        {/* Main content */}
        <div className="flex flex-col items-center justify-center">
          {/* Auth Form */}
          <div className="w-full max-w-md">
            {/* Logo */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="bg-white/20 rounded-full p-4">
                  <BookOpen className="text-white" size={44} strokeWidth={2.5} />
                </div>
              </div>
              <h1
                className="text-4xl font-black text-white drop-shadow-md"
                style={{ fontFamily: "Fredoka, sans-serif" }}
              >
                Bible Buds
              </h1>
              <p className="text-white/90 mt-1 font-semibold">
                {t('welcome')}
              </p>
            </div>

        {/* Card */}
        <div className="bg-white rounded-3xl p-8 shadow-2xl">
          {/* Toggle tabs */}
          <div className="flex bg-gray-100 rounded-2xl p-1 mb-6">
            <button
              onClick={() => { setMode("login"); setError(""); }}
              className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all ${mode === "login" ? "bg-[#118AB2] text-white shadow-md" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="login-tab"
            >
              {t('login')}
            </button>
            <button
              onClick={() => { setMode("register"); setError(""); }}
              className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all ${mode === "register" ? "bg-[#118AB2] text-white shadow-md" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="register-tab"
            >
              {t('register')}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">{t('name')}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#118AB2] transition-colors text-gray-800 font-medium"
                  placeholder={t('your_full_name')}
                  required
                  data-testid="name-input"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">{t('email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#118AB2] transition-colors text-gray-800 font-medium"
                placeholder={t('email_placeholder')}
                required
                data-testid="email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">{t('password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#118AB2] transition-colors text-gray-800 font-medium"
                placeholder={t('create_secure_password')}
                required
                minLength={6}
                data-testid="password-input"
              />
              {mode === "login" && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-[#118AB2] font-semibold hover:underline mt-2"
                  data-testid="forgot-password-link"
                >
                  {t('forgot_password')}
                </button>
              )}
            </div>

            {/* Plan Selection for Registration */}
            {mode === "register" && (
              <div className="pt-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">{t('choose_plan')}</label>
                <div className="space-y-2">
                  {plans.map((plan) => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
                        selectedPlan === plan.id 
                          ? "border-[#06D6A0] bg-[#06D6A0]/5" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      data-testid={`plan-${plan.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            selectedPlan === plan.id ? "border-[#06D6A0] bg-[#06D6A0]" : "border-gray-300"
                          }`}>
                            {selectedPlan === plan.id && <Check size={10} color="white" strokeWidth={3} />}
                          </div>
                          <div>
                            <p className="font-bold text-[#073B4C] text-sm">{plan.name}</p>
                            <p className="text-xs text-gray-500">{plan.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-black text-[#073B4C]">${plan.price}</p>
                          <p className="text-xs text-gray-400">/{plan.interval}</p>
                        </div>
                      </div>
                      {plan.savings && (
                        <div className="mt-1.5 inline-block bg-green-100 text-green-700 text-xs font-black px-2 py-0.5 rounded-full">
                          SAVE {plan.savings}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  7-day free trial included. Cancel anytime.
                </p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3">
                <p className="text-red-600 text-sm font-medium" data-testid="auth-error">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#06D6A0] hover:bg-[#05b588] text-white font-black py-4 rounded-2xl border-b-4 border-[#049a73] active:border-b-0 active:translate-y-1 transition-all disabled:opacity-60 text-lg mt-2"
              style={{ fontFamily: "Fredoka, sans-serif" }}
              data-testid="auth-submit-btn"
            >
              {loading ? (buttonText || t('loading')) : mode === "login" ? t('login_btn') : t('start_free_trial')}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-4">
            {mode === "login" ? t('no_account') + " " : t('have_account') + " "}
            <button
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-[#118AB2] font-bold hover:underline"
              data-testid="toggle-auth-mode"
            >
              {mode === "login" ? t('start_free_trial') : t('login_btn')}
            </button>
          </p>

          {/* Kids Login Link */}
          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <button
              onClick={() => navigate("/kids")}
              className="px-8 py-3 rounded-full font-black text-lg text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
              style={{ fontFamily: "Fredoka, sans-serif", background: "linear-gradient(135deg, #9B5DE5, #7B2FF7)" }}
              data-testid="kids-login-link"
            >
              {t('im_a_kid')}
            </button>
            <p className="text-xs text-gray-400 mt-2">
              {t('parents_sign_first')}
            </p>
          </div>
          
          {/* Support Email */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-400">
              {t('need_help')} <a href="mailto:support@biblebuds.space" className="text-[#118AB2] hover:underline">support@biblebuds.space</a>
            </p>
          </div>
        </div>

        {/* Promotional Flyer Section */}
        <div className="mt-6 bg-white rounded-3xl p-5 shadow-xl">
          <div className="text-center mb-4">
            <h3 className="font-black text-lg text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('discover_bible_buds')}
            </h3>
            <p className="text-sm text-gray-500">{t('watch_walkthrough')}</p>
          </div>
          
          {/* Video Walkthrough */}
          <div className="mb-4">
            <p className="text-xs font-bold text-[#118AB2] uppercase tracking-wide mb-2 text-center">{t('promo_video') || 'Promo Video'}</p>
            <div className="rounded-2xl overflow-hidden shadow-lg border-2 border-[#118AB2]/20">
              <video 
                controls 
                className="w-full"
                poster="/images/biblebuds-flyer.png"
                data-testid="auth-promo-video"
              >
                <source src="/bible-buds-promo.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <a href="/bible-buds-promo.mp4" download="BibleBuds-Promo.mp4"
              className="mt-2 inline-flex items-center gap-2 bg-gradient-to-r from-[#118AB2] to-[#06D6A0] text-white font-bold px-4 py-2 rounded-xl hover:opacity-90 transition-all text-sm w-full justify-center"
              data-testid="auth-download-video-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              {t('download_video') || 'Download Video'}
            </a>
          </div>
          
          {/* Flyer Image - Clickable to website */}
          <div className="flex justify-center mb-4">
            <a href="https://biblebuds.space" target="_blank" rel="noopener noreferrer">
              <img 
                src="/images/biblebuds-flyer.png"
                alt="Bible Buds - Daily Bible Lessons for Kids"
                className="w-56 h-auto object-contain rounded-2xl shadow-lg border-2 border-[#06D6A0]/30 hover:shadow-xl transition-shadow cursor-pointer"
                data-testid="auth-flyer-image"
              />
            </a>
          </div>
          
          {/* Download Button */}
          <div className="flex justify-center mb-4">
            <a 
              href="/images/biblebuds-flyer.png"
              download="BibleBuds-Flyer.png"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F77F00] to-[#FFD166] text-white font-bold px-5 py-2.5 rounded-xl hover:opacity-90 transition-all text-sm"
              data-testid="auth-download-flyer-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              {t('download_flyer') || 'Download Flyer'}
            </a>
          </div>
          
          {/* Social Share Buttons */}
          <div>
            <p className="text-xs text-gray-500 mb-2 text-center">{t('share_on') || 'Share on:'}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              <a href="https://www.facebook.com/sharer/sharer.php?u=https://biblebuds.space" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 bg-[#1877F2] text-white font-bold px-3 py-2 rounded-xl hover:opacity-90 text-xs">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </a>
              <a href="https://twitter.com/intent/tweet?text=Check%20out%20Bible%20Buds!%20Daily%20Bible%20lessons%20for%20kids!&url=https://biblebuds.space&hashtags=BibleBuds,ChristianParenting" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 bg-black text-white font-bold px-3 py-2 rounded-xl hover:opacity-90 text-xs">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                X / Twitter
              </a>
              <a href="https://wa.me/?text=Check%20out%20Bible%20Buds!%20Daily%20Bible%20lessons%20for%20kids!%20https://biblebuds.space" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 bg-[#25D366] text-white font-bold px-3 py-2 rounded-xl hover:opacity-90 text-xs">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
              <a href="mailto:?subject=Check%20out%20Bible%20Buds!&body=Daily%20Bible%20lessons%20for%20kids!%20https://biblebuds.space" 
                className="flex items-center gap-1 bg-gray-600 text-white font-bold px-3 py-2 rounded-xl hover:opacity-90 text-xs">
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                Email
              </a>
            </div>
          </div>
        </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
    </div>
  );
}

function ForgotPasswordModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { email });
      setNewPassword("sent"); // Just a flag to show success
    } catch (err) {
      setError(err.response?.data?.detail || "Could not reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
        <div className="text-center mb-6">
          <div className="bg-[#118AB2]/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <KeyRound size={32} color="#118AB2" />
          </div>
          <h2 className="text-2xl font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
            {newPassword ? "Check Your Email!" : "Forgot Password?"}
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            {newPassword 
              ? "We've sent you a new password" 
              : "Enter your email to reset your password"}
          </p>
        </div>

        {newPassword ? (
          <div className="space-y-4">
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 text-center">
              <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <Check size={24} className="text-green-600" />
              </div>
              <p className="text-green-700 font-bold mb-1">Email Sent!</p>
              <p className="text-green-600 text-sm">
                A new temporary password has been sent to <strong>{email}</strong>
              </p>
            </div>
            <p className="text-xs text-gray-500 text-center">
              Please check your inbox (and spam folder) and sign in with the new password.
            </p>
            <button
              onClick={onClose}
              className="w-full bg-[#118AB2] text-white font-black py-3 rounded-xl hover:bg-[#0d7a9e] transition-colors"
              style={{ fontFamily: "Fredoka, sans-serif" }}
              data-testid="close-reset-modal"
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#118AB2] transition-colors text-gray-800 font-medium"
                placeholder="parent@email.com"
                required
                data-testid="reset-email-input"
              />
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3">
                <p className="text-red-600 text-sm font-medium">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold py-3 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#118AB2] hover:bg-[#0d7a9e] text-white font-black py-3 rounded-xl transition-all disabled:opacity-60"
                style={{ fontFamily: "Fredoka, sans-serif" }}
                data-testid="reset-password-btn"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
